import { useState, type FormEvent } from 'react';
import { Link } from 'react-router-dom';
import { kuunda } from '@/lib/kuunda';
import { useI18n, type MessageKey } from '@/features/i18n/I18nProvider';
import { useToast } from '@/features/ui/toast/useToast';
import { trackConversion } from '@/features/analytics/tracker';
import { MarketingNav } from './MarketingNav';
import { MarketingFooter } from './MarketingFooter';
import { MarketingParcoursSteps } from './MarketingParcoursSteps';
import './marketing.css';

const offerDefs: {
  titleKey: MessageKey;
  textKey: MessageKey;
  altKey: MessageKey;
  img: string;
  objectPosition: string;
}[] = [
  {
    titleKey: 'home.offer1.title',
    textKey: 'home.offer1.text',
    altKey: 'home.offer1.alt',
    img: '/images/offre-conception.jpg',
    objectPosition: 'center 12%',
  },
  {
    titleKey: 'home.offer2.title',
    textKey: 'home.offer2.text',
    altKey: 'home.offer2.alt',
    img: '/images/offre-planification.jpg',
    objectPosition: 'center 40%',
  },
  {
    titleKey: 'home.offer3.title',
    textKey: 'home.offer3.text',
    altKey: 'home.offer3.alt',
    img: '/images/offre-pilotage.jpg',
    objectPosition: 'center 28%',
  },
  {
    titleKey: 'home.offer4.title',
    textKey: 'home.offer4.text',
    altKey: 'home.offer4.alt',
    img: '/images/offre-accompagnement.jpg',
    objectPosition: 'center 12%',
  },
];

const circleDefs: {
  titleKey: MessageKey;
  textKey: MessageKey;
  altKey: MessageKey;
  img: string;
}[] = [
  {
    titleKey: 'home.circle1.title',
    textKey: 'home.circle1.text',
    altKey: 'home.circle1.alt',
    img: '/images/africa-portrait.jpg',
  },
  {
    titleKey: 'home.circle2.title',
    textKey: 'home.circle2.text',
    altKey: 'home.circle2.alt',
    img: '/images/african-woman-business.jpg',
  },
  {
    titleKey: 'home.circle3.title',
    textKey: 'home.circle3.text',
    altKey: 'home.circle3.alt',
    img: '/images/entrepreneurs.jpg',
  },
];

const categoryDefs: { labelKey: MessageKey; img: string }[] = [
  { labelKey: 'home.cat.mode', img: '/images/mkt-mode.jpg' },
  { labelKey: 'home.cat.agro', img: '/images/mkt-agro.jpg' },
  { labelKey: 'home.cat.commerce', img: '/images/mkt-commerce.jpg' },
  { labelKey: 'home.cat.services', img: '/images/mkt-services.jpg' },
  { labelKey: 'home.cat.production', img: '/images/mkt-production.jpg' },
  { labelKey: 'home.cat.logistics', img: '/images/mkt-logistique.jpg' },
];

export function LandingPage() {
  const { t } = useI18n();

  return (
    <div className="mkt mkt-home">
      <MarketingNav />

      <section className="mkt-hero mkt-hero-photo">
        <img
          className="mkt-hero-bg"
          src="/images/hero-entrepreneurs.jpg"
          alt=""
          aria-hidden
        />
        <div className="mkt-hero-shade" aria-hidden />
        <div className="mkt-hero-inner">
          <p className="mkt-kicker">{t('home.hero.kicker')}</p>
          <h1 className="mkt-display">
            {t('home.hero.titleBefore')}
            <em>{t('home.hero.titleEm')}</em>
          </h1>
          <p className="lead">{t('home.hero.lead')}</p>
          <div className="mkt-cta">
            <Link to="/inscription" className="btn btn-accent">
              {t('home.hero.ctaStart')}
            </Link>
            <a href="#parcours" className="btn btn-ghost">
              {t('home.hero.ctaDiscover')}
            </a>
          </div>
        </div>
      </section>

      <section className="mkt-stats" aria-label={t('home.stats.aria')}>
        <div className="mkt-wrap mkt-stats-grid">
          <div>
            <strong>5</strong>
            <span>{t('home.stats.stepsLabel')}</span>
            <p>{t('home.stats.stepsText')}</p>
          </div>
          <div>
            <strong>15 000 €</strong>
            <span>{t('home.stats.fundingLabel')}</span>
            <p>{t('home.stats.fundingText')}</p>
          </div>
          <div>
            <strong>3</strong>
            <span>{t('home.stats.circlesLabel')}</span>
            <p>{t('home.stats.circlesText')}</p>
          </div>
        </div>
      </section>

      <section className="mkt-split mkt-wrap" id="parcours">
        <div className="mkt-split-media">
          <img src="/images/parcours-business.jpg" alt={t('home.parcours.alt')} />
        </div>
        <div className="mkt-split-copy">
          <p className="mkt-eyebrow">{t('home.parcours.eyebrow')}</p>
          <h2 className="mkt-section-title">{t('home.parcours.title')}</h2>
          <p className="mkt-section-lead">{t('home.parcours.lead')}</p>
        </div>
      </section>

      <section className="mkt-section mkt-section-parcours">
        <div className="mkt-wrap">
          <MarketingParcoursSteps />
        </div>
      </section>

      <section className="mkt-section mkt-section-alt" id="offres">
        <div className="mkt-wrap">
          <p className="mkt-eyebrow">{t('home.offres.eyebrow')}</p>
          <h2 className="mkt-section-title">{t('home.offres.title')}</h2>
          <p className="mkt-section-lead">{t('home.offres.lead')}</p>
          <div className="mkt-offer-grid">
            {offerDefs.map((o) => (
              <article key={o.titleKey} className="mkt-offer mkt-offer-media">
                <img
                  src={o.img}
                  alt={t(o.altKey)}
                  loading="lazy"
                  style={{ objectPosition: o.objectPosition }}
                />
                <div>
                  <h3>{t(o.titleKey)}</h3>
                  <p>{t(o.textKey)}</p>
                </div>
              </article>
            ))}
          </div>
        </div>
      </section>

      <section className="mkt-banner" id="concours">
        <img src="/images/west-africa-port.jpg" alt="" aria-hidden loading="lazy" />
        <div className="mkt-banner-shade" aria-hidden />
        <div className="mkt-wrap mkt-banner-copy">
          <p className="mkt-eyebrow">{t('home.concours.eyebrow')}</p>
          <h2 className="mkt-section-title">{t('home.concours.title')}</h2>
          <p className="mkt-section-lead">{t('home.concours.lead')}</p>
          <ul className="mkt-concours-points">
            <li>{t('home.concours.point1')}</li>
            <li>{t('home.concours.point2')}</li>
            <li>{t('home.concours.point3')}</li>
          </ul>
          <Link to="/inscription" className="btn btn-accent">
            {t('home.concours.cta')}
          </Link>
        </div>
      </section>

      <section className="mkt-section" id="marketplace">
        <div className="mkt-wrap">
          <p className="mkt-eyebrow">{t('home.marketplace.eyebrow')}</p>
          <h2 className="mkt-section-title">{t('home.marketplace.title')}</h2>
          <p className="mkt-section-lead">{t('home.marketplace.lead')}</p>
          <div className="mkt-mosaic" aria-label={t('home.marketplace.aria')}>
            {categoryDefs.map((c) => (
              <figure key={c.labelKey}>
                <img src={c.img} alt={t(c.labelKey)} loading="lazy" />
                <figcaption>{t(c.labelKey)}</figcaption>
              </figure>
            ))}
          </div>
          <Link to="/marketplace" className="btn btn-primary">
            {t('home.marketplace.cta')}
          </Link>
        </div>
      </section>

      <section className="mkt-banner mkt-banner-ambition">
        <img src="/images/africa-market-colors.jpg" alt="" aria-hidden loading="lazy" />
        <div className="mkt-banner-shade" aria-hidden />
        <div className="mkt-wrap mkt-banner-copy">
          <h2 className="mkt-section-title">{t('home.ambition.title')}</h2>
          <p className="mkt-section-lead">{t('home.ambition.lead')}</p>
        </div>
      </section>

      <section className="mkt-section mkt-section-alt" id="equipe">
        <div className="mkt-wrap">
          <p className="mkt-eyebrow">{t('home.team.eyebrow')}</p>
          <h2 className="mkt-section-title">{t('home.team.title')}</h2>
          <p className="mkt-section-lead">{t('home.team.lead')}</p>
          <div className="mkt-circle-grid">
            {circleDefs.map((c) => (
              <article key={c.titleKey} className="mkt-circle mkt-circle-media">
                <img src={c.img} alt={t(c.altKey)} loading="lazy" />
                <h3>{t(c.titleKey)}</h3>
                <p>{t(c.textKey)}</p>
              </article>
            ))}
          </div>
        </div>
      </section>

      <section className="mkt-final" id="contact">
        <div className="mkt-wrap">
          <h2 className="mkt-section-title">{t('home.final.title')}</h2>
          <p className="mkt-section-lead">{t('home.final.lead')}</p>
          <div className="mkt-cta">
            <Link to="/inscription" className="btn btn-accent">
              {t('home.final.ctaRegister')}
            </Link>
            <Link to="/contact" className="btn btn-ghost">
              {t('home.final.ctaContact')}
            </Link>
          </div>
        </div>
      </section>

      <MarketingFooter />
    </div>
  );
}

export function PresentationPage() {
  const { t } = useI18n();

  return (
    <div className="mkt mkt-page">
      <MarketingNav />
      <div className="mkt-page-hero">
        <img src="/images/africa-cowork.jpg" alt="" aria-hidden />
        <div className="mkt-page-hero-shade" aria-hidden />
        <div className="mkt-wrap mkt-page-hero-copy">
          <p className="mkt-eyebrow">{t('pres.eyebrow')}</p>
          <h1 className="mkt-section-title">{t('pres.title')}</h1>
        </div>
      </div>
      <main className="mkt-page-main">
        <div className="mkt-wrap">
          <div className="mkt-split mkt-split-tight">
            <div className="mkt-split-media">
              <img src="/images/africa-portrait.jpg" alt={t('pres.alt')} loading="lazy" />
            </div>
            <div className="mkt-split-copy">
              <p className="mkt-section-lead">{t('pres.lead')}</p>
            </div>
          </div>
          <MarketingParcoursSteps surface="none" />
          <Link to="/inscription" className="btn btn-primary">
            {t('pres.cta')}
          </Link>
        </div>
      </main>
      <MarketingFooter />
    </div>
  );
}

export function ContactPage() {
  const toast = useToast();
  const [form, setForm] = useState({ full_name: '', email: '', subject: '', body: '' });
  const [loading, setLoading] = useState(false);

  async function onSubmit(e: FormEvent) {
    e.preventDefault();
    setLoading(true);
    const { error: err } = await kuunda.from('contact_messages').insert({
      full_name: form.full_name.trim(),
      email: form.email.trim(),
      subject: form.subject.trim(),
      body: form.body.trim(),
    });
    setLoading(false);
    if (err) toast.error(err.message);
    else {
      trackConversion('contact', { subject: form.subject.trim() || null }, '/contact');
      toast.success('Message envoyé. Nous vous répondrons rapidement.');
      setForm({ full_name: '', email: '', subject: '', body: '' });
    }
  }

  return (
    <div className="mkt mkt-page">
      <MarketingNav />
      <div className="mkt-page-hero mkt-page-hero-short">
        <img src="/images/senegal-coast.jpg" alt="" aria-hidden />
        <div className="mkt-page-hero-shade" aria-hidden />
        <div className="mkt-wrap mkt-page-hero-copy">
          <p className="mkt-eyebrow">Contact</p>
          <h1 className="mkt-section-title">Écrivez-nous</h1>
        </div>
      </div>
      <main className="mkt-page-main">
        <div className="mkt-wrap mkt-contact-grid">
          <div className="mkt-contact-visual">
            <img
              src="/images/african-woman-business.jpg"
              alt="Équipe ISBD à l’écoute des entrepreneurs"
              loading="lazy"
            />
            <p className="muted">
              Une question sur le parcours, l’abonnement ou la marketplace ? Nous vous répondons
              rapidement.
            </p>
          </div>
          <form className="panel" onSubmit={onSubmit}>
            <div className="grid-2">
              <div className="field">
                <label>Nom</label>
                <input required value={form.full_name} onChange={(e) => setForm({ ...form, full_name: e.target.value })} />
              </div>
              <div className="field">
                <label>Email</label>
                <input
                  type="email"
                  required
                  value={form.email}
                  onChange={(e) => setForm({ ...form, email: e.target.value })}
                />
              </div>
            </div>
            <div className="field">
              <label>Objet</label>
              <input value={form.subject} onChange={(e) => setForm({ ...form, subject: e.target.value })} />
            </div>
            <div className="field">
              <label>Message</label>
              <textarea required rows={5} value={form.body} onChange={(e) => setForm({ ...form, body: e.target.value })} />
            </div>
            <button className="btn btn-primary" type="submit" disabled={loading}>
              {loading ? 'Envoi…' : 'Envoyer'}
            </button>
          </form>
        </div>
        <div className="mkt-wrap">
          <p className="stack-gap">
            Déjà membre ? <Link to="/connexion">Se connecter</Link>
          </p>
        </div>
      </main>
      <MarketingFooter />
    </div>
  );
}
